/**
 * Trees as pixel art (not tiles): each tree drawn once from its seed onto a
 * canvas at the tile art's density (TREE_PX art pixels a block), then shown
 * as one sprite, in the vines' style (see Vines.ts). A solid trunk in flat
 * bands with streaks of grain, tapering up from flared roots, limbs that fork
 * and thin, and a crown of outlined leaves, each lit on one half and shaded
 * on the other, in a few flat tones with a dark outline.
 *
 * Every species (TREE_SPECIES) is drawn the same way; what differs is its
 * shape (how its wood grows and where its leaf masses sit), its leaves and
 * its colours. Trunks come from the shared treeTrunk, so the trunk tiles the
 * game lays follow them.
 */

// Just the tree shapes, not the whole shared package: this runs in the tree worker.
import { treeTrunk, trunkPoint, type TreeStyle, type TreeTrunk } from "@dyson/shared/trees";

/** Art pixels per block (the tiles' own). */
export const TREE_PX = 32;
const PX = TREE_PX;
/** How many art pixels a detail drawn at 16 a block is now. */
const DETAIL = PX / 16;

interface Palette {
  /** Bark: outline, shadow, mid, light. */
  bark: [string, string, string, string];
  /** Leaves: outline, deep shadow, shadow, mid, light, highlight. */
  leaf: [string, string, string, string, string, string];
  /** Glowing fruit, if any. */
  glow?: string;
}

type P = { x: number; y: number };

/** What a species' shape gets to grow with (art pixels; y down; the root at (cx, H)). */
interface Builder {
  r: () => number;
  range: (a: number, b: number) => number;
  tall: number;
  unit: number;
  blocks: number;
  cx: number;
  H: number;
  trunk: TreeTrunk;
  trunkW: number;
  /** A point in the tree's own frame (blocks; x across, y up from the root) in art pixels. */
  art: (p: P) => P;
  /** Where along the trunk (0 root .. 1 top), which way it heads (radians, canvas), and its half-width there. */
  onTrunk: (t: number) => { p: P; angle: number; w: number };
  top: { p: P; angle: number; w: number };
  /** The standard trunk from treeTrunk, `wide` times its width, tapering to `taper` of that at the top, its roots flaring `flare` times the usual. */
  drawTrunk: (wide?: number, taper?: number, flare?: number) => void;
  /** A curving length of wood from p0 to p3 pulled by p1 and p2, half-widths w0 to w1. */
  curve: (p0: P, p1: P, p2: P, p3: P, w0: number, w1: number) => void;
  /** A limb from `from` heading `a` (radians, canvas), `len` long, bending `bend` radians over its length; its end. */
  limb: (from: P, a: number, len: number, w0: number, w1: number, bend?: number) => P;
  mass: (x: number, y: number, rx: number, ry: number) => void;
  /** A glowing point (fruit, ember, bud). */
  glow: (x: number, y: number) => void;
  /** Where the ground is under art column `x` (art pixels down; H where it's level with the root): roots reach down to it. */
  ground: (x: number) => number;
}

/** A species: its colours, its leaves, and its shape: how its wood grows and where its leaf masses sit. */
export interface TreeSpecies {
  id: TreeStyle;
  name: string;
  /** Where it grows, and why. */
  blurb: string;
  palette: Palette;
  /** Leaf length (blocks) and breadth (1: a vine's); needles lie along the boughs; hanging leaves point down; `up` ones reach upward. */
  leaf?: { len: number; wide: number; needles?: boolean; hang?: boolean; up?: boolean };
  /** Dark marks across pale bark (birch). */
  marks?: boolean;
  /** Foliage drawn its own way: a mushroom's cap; or none, a bare tree. */
  foliage?: "cap" | "none";
  build: (b: Builder) => void;
}

const dir = (a: number): P => ({ x: Math.cos(a), y: Math.sin(a) });
const add = (p: P, d: P, k: number): P => ({ x: p.x + d.x * k, y: p.y + d.y * k });
const UP = -Math.PI / 2;

/** Branches off the upper trunk as the game's broadleaf trees have, each ending in a clump; a willow's arc over and hang curtains. */
function broadleaf(b: Builder, o: { crown: number; branches?: [number, number]; willow?: boolean; reach?: number; bare?: boolean }): P[] {
  const { tall, r, range } = b;
  // A set number (a birch's three) takes nothing from the seed.
  const [few, most] = o.branches ?? [3, 5];
  const n = few === most ? few : Math.round(range(few, most));
  const crown = tall * o.crown;
  let side = r() < 0.5 ? -1 : 1;
  const tips: P[] = [];
  for (let k = 0; k < n; k++, side = -side) {
    const at = b.onTrunk(0.5 + (0.45 * (k + range(0.2, 0.8))) / n);
    const len = tall * range(0.22, 0.36) * (o.reach ?? 1);
    const a0 = at.angle + side * range(0.7, 1.15);
    const c1 = add(at.p, dir(a0 + side * range(0.15, 0.5)), len * 0.4);
    const c2 = add(c1, dir(a0 + side * range(0, 0.4)), len * 0.4);
    const curl = o.willow ? -range(0.9, 1.5) : range(0.5, 1.1);
    const end = add(c2, dir(a0 - side * curl), len * 0.35);
    const w = at.w * 0.55;
    b.curve(at.p, c1, c2, end, w, Math.max(2, w * 0.3));
    tips.push(end);
    if (r() < 0.5) {
      const from = add(at.p, { x: (c1.x - at.p.x + c2.x - at.p.x) / 2, y: (c1.y - at.p.y + c2.y - at.p.y) / 2 }, 1);
      const b0 = a0 + side * range(0.4, 0.9);
      const l2 = len * range(0.35, 0.5);
      const d1 = add(from, dir(b0), l2 * 0.5);
      const d2 = add(d1, dir(b0 - side * 0.4), l2 * 0.3);
      const e2 = add(d2, dir(b0 - side * range(0.9, 1.4)), l2 * 0.3);
      b.curve(from, d1, d2, e2, w * 0.45, 2);
      tips.push(e2);
    }
  }
  tips.push(b.top.p);
  if (o.bare) return tips;
  for (const tip of tips) {
    const s = crown * range(0.32, 0.45) * (tip === b.top.p ? 1.2 : 1);
    b.mass(tip.x, tip.y - s * 0.25, s, s * 0.8);
    if (o.willow) {
      const drop = tall * range(0.15, 0.3);
      b.mass(tip.x + range(-0.3, 0.3) * s, tip.y + drop / 2, b.unit * 0.35, drop / 2);
    }
  }
  return tips;
}

/** Tiers of boughs up a pine's trunk, widest at the bottom. */
function conifer(b: Builder): void {
  const { tall, unit, range } = b;
  const tiers = Math.round(4 + b.blocks / 7);
  for (let i = 0; i < tiers; i++) {
    const f = i / (tiers - 1);
    const { p } = b.onTrunk(0.22 + f * 0.76);
    const half = tall * 0.26 * (1 - f * 0.8) * range(0.9, 1.1);
    b.mass(p.x, p.y + half * 0.12, half, Math.max(unit * 0.35, half * 0.2));
  }
  b.mass(b.top.p.x, b.top.p.y - unit * 0.4, unit * 0.35, unit * 0.6);
}

/** Bare limbs that fork and fork again, thinning, like antlers or coral. */
function antlers(b: Builder, from: P, angle: number, len: number, w: number, depth: number, tips: P[]): void {
  const { range } = b;
  const end = b.limb(from, angle + range(-0.2, 0.2), len, w, Math.max(1.5, w * 0.6), range(-0.3, 0.3));
  if (depth === 0) {
    tips.push(end);
    return;
  }
  const spread = range(0.35, 0.65);
  antlers(b, end, angle - spread, len * range(0.62, 0.78), w * 0.65, depth - 1, tips);
  antlers(b, end, angle + spread, len * range(0.62, 0.78), w * 0.65, depth - 1, tips);
}

const OAK: Palette = { bark: ["#1e130b", "#3b2614", "#5a3b20", "#7a5532"], leaf: ["#0f2410", "#1d4419", "#2d6324", "#3f8430", "#62a83d", "#9fd15a"] };
const PINE: Palette = { bark: ["#1a100a", "#352013", "#4f321e", "#6b472b"], leaf: ["#0a1d17", "#123425", "#1b4a33", "#256342", "#3a8455", "#6db37a"] };
const BIRCH: Palette = { bark: ["#2a2622", "#8f8a80", "#cfcac0", "#efebe2"], leaf: ["#1e2a0c", "#3b5216", "#56731f", "#76962a", "#9dbb3c", "#d2e470"] };
const WILLOW: Palette = { bark: ["#1b140d", "#36291b", "#524029", "#6e5a3c"], leaf: ["#122214", "#24402a", "#355a37", "#4b7746", "#6c9a59", "#a8c97d"] };
const LANTERN: Palette = { bark: ["#1d120c", "#3d2416", "#5e3a22", "#83563a"], leaf: ["#0d2218", "#1a3f2a", "#26573a", "#357550", "#4f9a68", "#8acc92"], glow: "#ffd27a" };
const FROST: Palette = { bark: ["#1b2028", "#3a4554", "#5d6d80", "#8796aa"], leaf: ["#1c2a3a", "#3a5670", "#5a7f9c", "#80a8c4", "#b0d2e6", "#eaf6ff"] };
const DUSK: Palette = { bark: ["#140e18", "#2c1e33", "#43304c", "#5d4668"], leaf: ["#140c22", "#2a1a45", "#3d2763", "#563a85", "#7b56ad", "#b48ce0"], glow: "#8ff0ff" };

/** Every species the rocks grow. */
export const TREE_SPECIES: readonly TreeSpecies[] = [
  { id: "oak", name: "Oak", palette: OAK, blurb: "Meadows and groves: a stout trunk, branches sweeping out and up, a round crown.", build: (b) => void (b.drawTrunk(), broadleaf(b, { crown: 0.22 })) },
  { id: "pine", name: "Pine", palette: PINE, leaf: { len: 0.5, wide: 0.4, needles: true }, blurb: "Lichen and frost rocks: a tall trunk strung with tiers of drooping boughs.", build: (b) => void (b.drawTrunk(), conifer(b)) },
  { id: "birch", name: "Birch", palette: BIRCH, leaf: { len: 0.5, wide: 1 }, blurb: "Meadows: slender, pale bark marked dark, a light crown of three branches.", marks: true, build: (b) => void (b.drawTrunk(), broadleaf(b, { crown: 0.16, branches: [3, 3], reach: 0.8 })) },
  { id: "willow", name: "Willow", palette: WILLOW, leaf: { len: 0.7, wide: 0.55, hang: true }, blurb: "Groves, by water: branches arc over and hang curtains of leaves.", build: (b) => void (b.drawTrunk(), broadleaf(b, { crown: 0.2, willow: true })) },
  { id: "lantern", name: "Lantern", palette: LANTERN, blurb: "Home and the wrecks: an oak's shape with warm glowing fruit under its crown.", build: (b) => void (b.drawTrunk(), broadleaf(b, { crown: 0.22 })) },
  { id: "frost", name: "Frost", palette: FROST, blurb: "Frost rocks: grey-blue bark and an ice-white crown.", build: (b) => void (b.drawTrunk(), broadleaf(b, { crown: 0.22 })) },
  { id: "dusk", name: "Dusk", palette: DUSK, blurb: "Twilight rocks: violet foliage with cold blue fruit.", build: (b) => void (b.drawTrunk(), broadleaf(b, { crown: 0.22 })) },

  {
    id: "candelabra", name: "Candelabra tree",
    palette: { bark: ["#1a1510", "#3a3024", "#5a4a38", "#7a6650"], leaf: ["#1f2a0a", "#3a4a10", "#5a6e18", "#7f9424", "#a9bb38", "#e2e46a"] },
    blurb: "The trunk ends in a row of arms that turn and rise together like a candelabra, each holding a tall flame of yellow-green leaves. Meadows and home, where the builders liked their trees orderly.",
    leaf: { len: 0.5, wide: 0.6, up: true },
    build: (b) => {
      b.drawTrunk(1, 0.8, 1.2);
      const { top, tall, unit, range } = b;
      const n = 5;
      for (let i = 0; i < n; i++) {
        const f = i / (n - 1) - 0.5;
        const len = tall * (0.16 + Math.abs(f) * 0.3);
        const a0 = UP + f * 2.2;
        const c1 = add(top.p, dir(a0), len * 0.45);
        const c2 = add(c1, dir(UP + f * 0.5), len * 0.3);
        const end = add(c2, dir(UP), len * 0.35);
        b.curve(top.p, c1, c2, end, top.w * 0.55, Math.max(2, top.w * 0.25));
        const h = unit * range(1.5, 1.9) * (1 - Math.abs(f) * 0.3);
        b.mass(end.x, end.y - h * 0.6, unit * 0.7, h);
      }
    },
  },
  {
    id: "pagoda", name: "Pagoda tree",
    palette: { bark: ["#1e0f0a", "#4a2418", "#74392a", "#9a5540"], leaf: ["#0a1e1c", "#123a34", "#1c5a4e", "#287a66", "#3e9c82", "#8ad6b4"] },
    blurb: "A straight red trunk with whorls of level branches at intervals, each whorl a flat plate of leaves, smaller toward the top, open air between the tiers. Groves and the sunward slopes of lichen rocks.",
    leaf: { len: 0.45, wide: 0.7 },
    build: (b) => {
      b.drawTrunk(0.85, 0.35, 0.8);
      const { tall, unit, range } = b;
      const tiers = 5;
      for (let i = 0; i < tiers; i++) {
        const f = i / (tiers - 1);
        const at = b.onTrunk(0.3 + f * 0.65);
        const len = tall * 0.24 * (1 - f * 0.72) * range(0.9, 1.1);
        for (const side of [-1, 1]) {
          const end = b.limb(at.p, side > 0 ? -0.08 : Math.PI + 0.08, len, at.w * 0.45, 2, side * -0.25);
          const mid = { x: (at.p.x + end.x) / 2, y: (at.p.y + end.y) / 2 - unit * 0.3 };
          b.mass(mid.x, mid.y, len * 0.6, unit * 0.55);
          b.mass(end.x, end.y - unit * 0.25, unit * 0.6, unit * 0.45);
        }
      }
      b.mass(b.top.p.x, b.top.p.y - unit * 0.4, unit * 0.4, unit * 0.5);
    },
  },
  {
    id: "stilt", name: "Stilt tree",
    palette: { bark: ["#1a1a16", "#3e3c34", "#5e5c50", "#7e7c6c"], leaf: ["#0c2412", "#174424", "#226233", "#2f8445", "#4aa85c", "#8ad488"] },
    blurb: "Stands on a cage of stilt roots arching down to the rock, the trunk starting a few blocks up, with a broad flat crown. Grove shores and the wet rocks; you can walk under it.",
    leaf: { len: 0.6, wide: 1 },
    build: (b) => {
      b.drawTrunk(0.8, 0.5, 0);
      const { tall, unit, range, H } = b;
      const n = Math.round(range(5, 7));
      for (let i = 0; i < n; i++) {
        const at = b.onTrunk(range(0.08, 0.28));
        const dx = (i % 2 ? 1 : -1) * tall * range(0.08, 0.22) * (i < 2 ? 0.6 : 1);
        const foot = { x: at.p.x + dx, y: b.ground(at.p.x + dx) + unit * 0.3 };
        b.curve(at.p, { x: at.p.x + dx * 0.35, y: at.p.y + unit * 0.1 }, { x: at.p.x + dx * 0.9, y: H - unit * 0.9 }, foot, at.w * 0.4, Math.max(2, at.w * 0.3));
      }
      const { top } = b;
      for (const f of [-1, -0.35, 0.35, 1]) {
        const end = b.limb(top.p, UP + f * 1.25, tall * (0.14 + Math.abs(f) * 0.14), top.w * 0.6, 2, -f * 0.5);
        b.mass(end.x, end.y - unit * 0.2, tall * 0.13, unit * 0.6);
      }
      b.mass(top.p.x, top.p.y - unit * 0.7, tall * 0.3, unit * 0.65);
    },
  },
  {
    id: "braid", name: "Braided tree",
    palette: { bark: ["#20170e", "#4a3620", "#6e5232", "#927048"], leaf: ["#1a1c0a", "#38401a", "#566426", "#788a34", "#9fb04a", "#d6e07e"] },
    blurb: "Two trunks wound round each other from one root, parting near the top into two small crowns leaning away. Meadows; the pair grows from one seed.",
    leaf: { len: 0.55, wide: 0.9 },
    build: (b) => {
      const { trunk, trunkW, tall, unit, range, art } = b;
      const [p0, p1, p2, p3] = trunk.pts;
      const swayX = Math.max(Math.abs(p1.x), tall / PX / 8);
      const sign = p1.x >= 0 ? 1 : -1;
      const tops: P[] = [];
      for (const s of [1, -1]) {
        const a = art({ x: p0.x, y: p0.y });
        const c1 = art({ x: s * sign * swayX * 1.5, y: p1.y });
        const c2 = art({ x: -s * sign * swayX * 1.3, y: p2.y });
        const top = art({ x: p3.x + s * sign * swayX * 0.9, y: p3.y * 1.05 });
        b.curve(a, c1, c2, top, trunkW * 0.75, trunkW * 0.3);
        tops.push(top);
        const lean = Math.atan2(top.y - c2.y, top.x - c2.x);
        for (const f of [-0.8, 0.5]) {
          const end = b.limb(top, lean + f, tall * range(0.12, 0.18), trunkW * 0.3, 2, -f * 0.4);
          b.mass(end.x, end.y - unit * 0.4, unit * range(1.1, 1.4), unit * 1.05);
        }
        b.mass(top.x, top.y - unit * 0.8, unit * 1.6, unit * 1.3);
      }
    },
  },
  {
    id: "bell", name: "Copper bell",
    palette: { bark: ["#1c120a", "#3e2a16", "#5e4324", "#7e5c34"], leaf: ["#2a140a", "#5a2a10", "#8a4418", "#b86a22", "#d9933a", "#f4c76a"] },
    blurb: "Ribs arch out from the top of the trunk like an umbrella's and copper leaves hang from them in a bell, hollow underneath: a dry room to stand in. Groves and wreck rocks.",
    leaf: { len: 0.65, wide: 0.55, hang: true },
    build: (b) => {
      b.drawTrunk(1, 0.6);
      const { top, tall, unit, range } = b;
      const n = 7;
      for (let i = 0; i < n; i++) {
        const a = -Math.PI + ((i + 0.5) / n) * Math.PI + range(-0.08, 0.08);
        const out = Math.cos(a) >= 0 ? 1 : -1;
        const len = tall * range(0.26, 0.34) * (0.7 + 0.3 * Math.abs(Math.cos(a)));
        const c1 = add(top.p, dir(a), len * 0.4);
        const c2 = add(c1, dir(a + out * 0.8), len * 0.35);
        const end = add(c2, dir(Math.PI / 2 - out * 0.5), len * 0.3);
        b.curve(top.p, c1, c2, end, top.w * 0.5, 2);
        const drop = tall * range(0.16, 0.26) * (0.6 + 0.4 * Math.abs(Math.cos(a)));
        b.mass(end.x, end.y + drop * 0.45, unit * 0.45, drop * 0.5);
        b.mass((c2.x + end.x) / 2, (c2.y + end.y) / 2 + unit * 0.3, unit * 0.4, unit * 0.6);
      }
      b.mass(top.p.x, top.p.y - unit * 0.1, unit * 0.9, unit * 0.5);
    },
  },
  {
    id: "thorn", name: "Thorn tree",
    palette: { bark: ["#141210", "#302a22", "#4c4436", "#6a6050"], leaf: ["#141e14", "#2a3a26", "#3f5636", "#5a7448", "#7c9660", "#b4c892"] },
    blurb: "Zigzag limbs that turn sharply at every joint, long bare thorns at the turns and only small tufts of grey-green leaf. Lichen rocks and the dry side of meadows; goes up like tinder.",
    leaf: { len: 0.4, wide: 0.8 },
    build: (b) => {
      b.drawTrunk(0.9, 0.6);
      const { tall, unit, range, r } = b;
      const zig = (from: P, a: number, len: number, w: number, steps: number) => {
        let p = from;
        let ang = a;
        for (let k = 0; k < steps; k++) {
          const l = len * (1 - k * 0.15);
          const end = b.limb(p, ang, l, w * (1 - k * 0.2), Math.max(1.5, w * (0.8 - k * 0.2)));
          // A thorn at the joint, out the far side of the turn.
          const turn = (k % 2 ? 1 : -1) * range(0.7, 1.1);
          b.limb(end, ang - turn * 1.6, unit * range(0.6, 0.95), 3.5, 1);
          if (r() < 0.8) b.mass(end.x, end.y - unit * 0.25, unit * range(0.55, 0.8), unit * 0.5);
          p = end;
          ang += turn;
        }
      };
      const n = Math.round(range(3, 4));
      for (let i = 0; i < n; i++) {
        const at = b.onTrunk(0.45 + (0.5 * (i + 0.5)) / n);
        const side = i % 2 ? 1 : -1;
        zig(at.p, at.angle + side * range(0.9, 1.3), tall * range(0.1, 0.14), at.w * 0.5, 4);
      }
      zig(b.top.p, UP + range(-0.3, 0.3), tall * 0.1, b.top.w * 0.6, 3);
    },
  },
  {
    id: "halo", name: "Halo tree",
    palette: { bark: ["#121420", "#2a2e44", "#434a68", "#5e678a"], leaf: ["#162236", "#2a4060", "#3c5f8a", "#5a86b4", "#86b4d6", "#c8ecf8"] },
    blurb: "The crown is a ring: spokes run from the trunk's top to a wheel of pale blue leaves with open sky through the middle. Twilight rocks and the zero-g ones, where nothing says which way up a crown should grow.",
    leaf: { len: 0.55, wide: 0.8 },
    build: (b) => {
      b.drawTrunk(1, 0.45);
      const { top, tall, unit, range } = b;
      const R = tall * 0.23;
      const c = { x: top.p.x, y: top.p.y - R * 0.75 };
      const spokes = 5;
      for (let i = 0; i < spokes; i++) {
        const a = -Math.PI / 2 + ((i + 0.5) / spokes - 0.5) * 2 * Math.PI * 0.8 + range(-0.1, 0.1);
        const rim = { x: c.x + Math.cos(a) * R, y: c.y + Math.sin(a) * R * 0.85 };
        const mid = { x: (top.p.x + rim.x) / 2 + range(-0.2, 0.2) * unit, y: (top.p.y + rim.y) / 2 };
        b.curve(top.p, mid, mid, rim, top.w * 0.5, 2);
      }
      const n = 12;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + range(-0.1, 0.1);
        b.mass(c.x + Math.cos(a) * R, c.y + Math.sin(a) * R * 0.85, unit * range(0.9, 1.1), unit * range(0.75, 0.9));
      }
    },
  },
  {
    id: "kelp", name: "Sky kelp",
    palette: { bark: ["#1c1e0a", "#3a3e14", "#585c20", "#76792e"], leaf: ["#1c200a", "#3a3e12", "#5a5e1c", "#7c7f2a", "#a2a040", "#d0c86a"], glow: "#eef4b8" },
    blurb: "One tall swaying stem with long olive blades alternating up its length and pale bladders at the nodes, like kelp reaching for the sun. Zero-g rocks and the open side of groves, in stands.",
    leaf: { len: 0.7, wide: 0.5, hang: true },
    build: (b) => {
      b.drawTrunk(0.6, 0.45, 0.6);
      const { tall, unit, range } = b;
      let side = b.r() < 0.5 ? -1 : 1;
      for (let t = 0.18; t <= 1.0; t += 0.085, side = -side) {
        const at = b.onTrunk(Math.min(1, t));
        const len = unit * range(1.7, 2.3) * (1 - t * 0.3);
        const end = b.limb(at.p, side > 0 ? -0.5 : Math.PI + 0.5, len * 0.5, at.w * 0.35, 1.5, side * 0.6);
        b.mass((at.p.x + end.x) / 2 + side * len * 0.3, (at.p.y + end.y) / 2 + unit * 0.35, len * 0.6, unit * 0.7);
        if (t > 0.3 && b.r() < 0.5) b.glow(at.p.x - side * at.w * 0.4, at.p.y);
      }
      b.mass(b.top.p.x, b.top.p.y - unit * 0.5, unit * 0.4, unit * 0.7);
    },
  },
  {
    id: "cloud", name: "Cloud pine",
    palette: { bark: ["#1a100a", "#3a2416", "#5a3a24", "#7a5436"], leaf: ["#0a1d17", "#123425", "#1b4a33", "#256342", "#3a8455", "#6db37a"] },
    blurb: "A leaning pine with a few long level branches, each ending in a flat pad of needles like a cloud, air between the pads. The home rock's gardens, trained for years.",
    leaf: { len: 0.4, wide: 0.4, needles: true },
    build: (b) => {
      b.drawTrunk(1.1, 0.5);
      const { tall, unit, range } = b;
      const n = Math.round(range(3, 4));
      let side = b.r() < 0.5 ? -1 : 1;
      for (let i = 0; i < n; i++, side = -side) {
        const at = b.onTrunk(0.4 + (0.55 * (i + 0.3)) / n);
        const len = tall * range(0.2, 0.3) * (1 - i * 0.12);
        const end = b.limb(at.p, side > 0 ? -0.15 : Math.PI + 0.15, len, at.w * 0.5, 2.5, side * -0.35);
        b.mass(end.x, end.y - unit * 0.35, tall * 0.1, unit * 0.42);
        const mid = { x: at.p.x + (end.x - at.p.x) * 0.5, y: at.p.y + (end.y - at.p.y) * 0.5 };
        if (b.r() < 0.6) b.mass(mid.x, mid.y - unit * 0.45, tall * 0.06, unit * 0.32);
      }
      b.mass(b.top.p.x, b.top.p.y - unit * 0.4, tall * 0.09, unit * 0.45);
    },
  },

  {
    id: "starbark", name: "Starbark",
    palette: { bark: ["#0f0b1c", "#1f1838", "#2c2448", "#4a3e78"], leaf: ["#0c2e2c", "#14423f", "#1d5e5a", "#2a8a7e", "#38a896", "#7fe0c8"], glow: "#9fffd0" },
    blurb: "The tree the old blocks promised: indigo bark, teal fronds in pom-pom clumps on long bare branches, glow bulbs hanging under them. Twilight and home.",
    leaf: { len: 0.7, wide: 0.6 },
    build: (b) => {
      b.drawTrunk(0.9, 0.45);
      const tips = broadleaf(b, { crown: 0.19, branches: [4, 6], reach: 1.15 });
      for (const t of tips) for (let k = 0, n = Math.round(b.range(1, 3)); k < n; k++) {
        const x = t.x + b.range(-0.6, 0.6) * b.unit;
        const y = t.y + b.unit * b.range(0.5, 0.9);
        b.curve(t, { x, y: t.y + (y - t.y) * 0.5 }, { x, y }, { x, y }, 1.5, 1.5);
        b.glow(x, y + DETAIL * 2);
      }
    },
  },
  {
    id: "palm", name: "Fan palm",
    palette: { bark: ["#2a1c10", "#5a4028", "#86643c", "#ad8a58"], leaf: ["#102a14", "#1f4a22", "#2f6a30", "#45903f", "#6ab352", "#a6d86e"] },
    blurb: "A bare ringed trunk and a burst of long fronds arcing out and drooping from the top. Meadows and the sunny side of groves.",
    leaf: { len: 0.55, wide: 0.45, needles: true },
    build: (b) => {
      b.drawTrunk(1, 0.7);
      const { top, tall, unit, range } = b;
      const n = Math.round(range(7, 9));
      for (let i = 0; i < n; i++) {
        const a = -Math.PI + ((i + 0.5) / n) * Math.PI + range(-0.1, 0.1);
        const out = Math.cos(a) >= 0 ? 1 : -1;
        const len = tall * range(0.3, 0.42);
        const c1 = add(top.p, dir(a), len * 0.45);
        const c2 = add(c1, dir(a + out * 0.9), len * 0.35);
        const end = add(c2, dir(Math.PI / 2 - out * 0.4), len * 0.3);
        b.curve(top.p, c1, c2, end, Math.max(2, unit * 0.12), 1.5);
        for (let k = 1; k <= 5; k++) {
          const t = k / 5;
          const u = 1 - t;
          const p = { x: u * u * u * top.p.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * end.x, y: u * u * u * top.p.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * end.y };
          b.mass(p.x, p.y, unit * (0.55 - t * 0.15), unit * 0.32);
        }
      }
      for (let k = 0; k < 3; k++) b.glow(top.p.x + range(-0.5, 0.5) * unit, top.p.y + unit * range(0.2, 0.5));
    },
  },
  {
    id: "acacia", name: "Umbrella tree",
    palette: { bark: ["#1c1208", "#3f2a12", "#664520", "#8a6334"], leaf: ["#152a0e", "#2c4f1a", "#3e6e24", "#5a9130", "#80b548", "#b8dc6c"] },
    blurb: "A short trunk forking into limbs that spread wide, under a flat-topped canopy in layers. Lichen rocks and dry meadows.",
    leaf: { len: 0.55, wide: 0.8 },
    build: (b) => {
      b.drawTrunk(1.1, 0.6);
      const { top, tall, unit, range } = b;
      const limbs = Math.round(range(3, 4));
      for (let i = 0; i < limbs; i++) {
        const f = limbs === 1 ? 0 : i / (limbs - 1) - 0.5;
        const a = UP + f * range(1.6, 2.1);
        const len = tall * range(0.28, 0.38);
        const c1 = add(top.p, dir(a), len * 0.4);
        const c2 = add(c1, dir(a * 0.6 + UP * 0.4), len * 0.35);
        const end = add(c2, dir(UP + f * 0.5), len * 0.25);
        b.curve(top.p, c1, c2, end, top.w * 0.7, 2);
        b.mass(end.x, end.y - unit * 0.3, tall * range(0.14, 0.2), unit * 0.55);
      }
      const y = top.p.y - tall * 0.22;
      b.mass(top.p.x, y, tall * 0.44, unit * 0.7);
      b.mass(top.p.x - tall * 0.12, y + unit * 0.9, tall * 0.26, unit * 0.6);
      b.mass(top.p.x + tall * 0.14, y + unit * 1.0, tall * 0.22, unit * 0.55);
    },
  },
  {
    id: "cypress", name: "Spire cypress",
    palette: { bark: ["#1a1208", "#3a2a14", "#5a4322", "#7a5e34"], leaf: ["#0b1f14", "#143a22", "#1e5230", "#2a6c3c", "#3f8a4e", "#6fae6a"] },
    blurb: "A tall narrow flame of foliage close round the trunk, widest a third of the way up. Groves and home, in rows.",
    leaf: { len: 0.4, wide: 0.6, up: true },
    build: (b) => {
      b.drawTrunk(0.8, 0.3);
      const { tall, unit } = b;
      for (let f = 0.1; f <= 1.02; f += 0.07) {
        const { p } = b.onTrunk(Math.min(1, f));
        const half = (tall * 0.13 * Math.sin(Math.PI * Math.min(1, (f + 0.05) / 1.05) ** 0.6) + unit * 0.2) * b.range(0.9, 1.1);
        b.mass(p.x + b.range(-0.2, 0.2) * unit, p.y, half, unit * 0.55);
      }
      b.mass(b.top.p.x, b.top.p.y - unit * 0.5, unit * 0.25, unit * 0.7);
    },
  },
  {
    id: "capTree", name: "Giant puffcap", foliage: "cap",
    palette: { bark: ["#3a2a3a", "#8a7a86", "#c4b4bc", "#e8dce2"], leaf: ["#3e1236", "#5e1e50", "#7d2a6b", "#a33a8c", "#c050a8", "#f2b8e6"] },
    blurb: "A puffcap grown to a tree: a thick pale stalk under a wide spotted magenta cap, gills beneath. Dark and twilight rocks; its spores still heal.",
    build: (b) => {
      b.drawTrunk(1.9, 0.85, 0.5);
      b.mass(b.top.p.x, b.top.p.y - b.unit * 0.2, b.tall * b.range(0.36, 0.44), b.tall * 0.19);
    },
  },
  {
    id: "ember", name: "Ember tree", foliage: "none",
    palette: { bark: ["#0d0907", "#231a14", "#3a2c22", "#53403a"], leaf: ["#000", "#000", "#000", "#000", "#000", "#000"], glow: "#ff8a2a" },
    blurb: "A dead tree on the ash rocks near the suns, charred black, embers still glowing in cracks along its limbs. Nothing to harvest but charcoal.",
    build: (b) => {
      b.drawTrunk(1, 0.4);
      const tips = broadleaf(b, { crown: 0, branches: [4, 6], reach: 0.9, bare: true });
      for (const t of tips) if (b.r() < 0.7) b.glow(t.x, t.y);
      for (let k = 0; k < 10; k++) {
        const at = b.onTrunk(b.range(0.1, 0.9));
        b.glow(at.p.x + b.range(-0.7, 0.7) * at.w, at.p.y);
      }
    },
  },
  {
    id: "antler", name: "Antler tree", foliage: "none",
    palette: { bark: ["#2a2a30", "#8a8894", "#c8c6d0", "#efeef4"], leaf: ["#000", "#000", "#000", "#000", "#000", "#000"], glow: "#7fe0c8" },
    blurb: "Pale bone-white limbs forking again and again like coral or antlers, each tip budding teal light. Twilight and dark rocks: a tree, or something that only looks like one.",
    build: (b) => {
      b.drawTrunk(0.9, 0.7);
      const tips: P[] = [];
      const n = Math.round(b.range(2, 3));
      for (let i = 0; i < n; i++) antlers(b, b.top.p, UP + (i / (n - 1) - 0.5) * 1.3 + b.range(-0.15, 0.15), b.tall * 0.22, b.top.w * 0.8, 3, tips);
      for (const t of tips) b.glow(t.x, t.y);
    },
  },
  {
    id: "baobab", name: "Bottle tree",
    palette: { bark: ["#241a10", "#4a3824", "#6e5638", "#917552"], leaf: ["#172a12", "#2d4e1e", "#427029", "#5c9238", "#7fb64c", "#b4dc70"] },
    blurb: "A fat bottle of a trunk, short and swollen, with stubby branches and a sparse crown. Lichen rocks: it stores its water.",
    leaf: { len: 0.5, wide: 0.9 },
    build: (b) => {
      b.drawTrunk(2.3, 0.35, 0.35);
      const { top, tall, unit, range } = b;
      const n = Math.round(range(4, 6));
      for (let i = 0; i < n; i++) {
        const f = i / (n - 1) - 0.5;
        const end = b.limb(top.p, UP + f * 2.2 + range(-0.1, 0.1), tall * range(0.12, 0.2), top.w * 0.5, 2, -f * 1.2);
        b.mass(end.x, end.y - unit * 0.2, unit * range(0.7, 1.1), unit * 0.6);
      }
    },
  },
];

const BY_STYLE = Object.fromEntries(TREE_SPECIES.map((s) => [s.id, s])) as Record<TreeStyle, TreeSpecies>;

/** The colour (0..1) a species' leaves are when they come off it: their mid tone. Null for a bare tree. */
export function leafColour(style: TreeStyle): readonly [number, number, number] | null {
  const sp = BY_STYLE[style];
  if (sp.foliage === "none") return null;
  const h = sp.palette.leaf[3];
  return [parseInt(h.slice(1, 3), 16) / 255, parseInt(h.slice(3, 5), 16) / 255, parseInt(h.slice(5, 7), 16) / 255];
}

const NONE = 0;
const WOOD = 1;
const LEAF = 2;
const FRUIT = 3;

/** A short straight piece of a curving length of wood. */
interface Branch {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Half-width at each end (art pixels). */
  w0: number;
  w1: number;
  /** How far along its curve it starts (art pixels), so grain runs on unbroken from piece to piece. */
  s0: number;
  /** The trunk: its roots flare out over this far from the ground (0: not the trunk). */
  flare: number;
}

/** A mass of the crown: filled with leaf clusters. */
interface Mass {
  x: number;
  y: number;
  rx: number;
  ry: number;
}

/** A small, fast, seeded random (mulberry32). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 4x4 ordered dither: blends two tones in a pixel pattern rather than a smooth gradient. */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16 - 0.5);

/**
 * The ground under a tree: how far below its root (blocks) the ground is at
 * points `step` blocks apart across it, the middle one under the root
 * (positive where it falls away; 0 where it's level or higher).
 */
export interface TreeGround {
  step: number;
  depth: number[];
}

/** A drawn tree: its pixels (RGBA, top row first), and where its root is (art pixels from the top left): stand that on the ground. */
export interface TreePixels {
  data: Uint8ClampedArray<ArrayBuffer>;
  width: number;
  height: number;
  rootX: number;
  rootY: number;
}

export interface TreeSprite {
  canvas: HTMLCanvasElement;
  /** Where the root is in the canvas (art pixels from its top left): stand this on the ground. */
  rootX: number;
  rootY: number;
}

/** A tree on a canvas (the preview page draws them so; the game has them drawn off the main thread, see treeWorker). */
export function drawTree(style: TreeStyle, seed: number, blocks: number): TreeSprite {
  const art = treePixels(style, seed, blocks);
  const canvas = document.createElement("canvas");
  canvas.width = art.width;
  canvas.height = art.height;
  canvas.getContext("2d")!.putImageData(new ImageData(art.data, art.width, art.height), 0, 0);
  return { canvas, rootX: art.rootX, rootY: art.rootY };
}

/**
 * Draw a tree about `blocks` tall from `seed`. It grows up the canvas from
 * its root (rootX, rootY); turn the sprite about that point to stand it on
 * curved ground. Same seed, same tree. Given the `ground` it stands on, its
 * roots grow down to meet it where it falls away; without, it's level.
 */
export function treePixels(style: TreeStyle, seed: number, blocks: number, ground?: TreeGround): TreePixels {
  const sp = BY_STYLE[style];
  const r = rng(seed);
  const range = (a: number, b: number) => a + (b - a) * r();
  const pal = sp.palette;
  const tall = Math.round(blocks * PX);
  /** Size of a block's worth of tree, scaled gently with its height. */
  const unit = PX * (blocks / 12) ** 0.5;
  // Laid out with room to spare (cropped to the tree at the end): the root at (cx, H).
  const W = Math.round(tall * 2.2);
  const H = Math.round(tall * 1.35);
  const cx = W / 2;
  // Below the root, room for roots reaching down to the ground.
  const deepest = Math.max(0, ...(ground?.depth ?? []));
  const HH = H + Math.ceil(deepest * PX + unit * 2);
  const groundY = (x: number) => {
    if (!ground?.depth.length) return H;
    const d = ground.depth;
    const k = Math.max(0, Math.min(d.length - 1, (x - cx) / PX / ground.step + (d.length - 1) / 2));
    const i = Math.min(d.length - 2, Math.floor(k));
    return i < 0 ? H + d[0]! * PX : H + (d[i]! + (d[i + 1]! - d[i]!) * (k - i)) * PX;
  };
  const pieces: Branch[] = [];
  const masses: Mass[] = [];
  const glows: P[] = [];

  const art = (p: P): P => ({ x: cx + p.x * PX, y: H - p.y * PX });
  const curve = (p0: P, p1: P, p2: P, p3: P, w0: number, w1: number, root = 0) => {
    const n = 28;
    let prev = p0;
    let s = 0;
    for (let k = 1; k <= n; k++) {
      const t = k / n;
      const u = 1 - t;
      const p = { x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x, y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y };
      pieces.push({ x0: prev.x, y0: prev.y, x1: p.x, y1: p.y, w0: w0 + (w1 - w0) * ((k - 1) / n), w1: w0 + (w1 - w0) * t, s0: s, flare: root * w0 * 2.5 });
      s += Math.hypot(p.x - prev.x, p.y - prev.y);
      prev = p;
    }
  };
  const trunk = treeTrunk(style, seed, blocks);
  const trunkW = trunk.half * PX;
  const onTrunk = (t: number) => {
    const p = art(trunkPoint(trunk, t));
    const q = art(trunkPoint(trunk, Math.min(1, t + 0.01)));
    const back = art(trunkPoint(trunk, Math.max(0, t - 0.01)));
    return { p, angle: Math.atan2(q.y - back.y, q.x - back.x), w: trunkW * (1 - t * 0.5) };
  };
  const b: Builder = {
    r, range, tall, unit, blocks, cx, H, trunk, trunkW, art, onTrunk, top: onTrunk(1),
    drawTrunk: (wide = 1, taper = 0.5, flare = 1) => {
      const [t0, t1, t2, t3] = trunk.pts.map(art) as [P, P, P, P];
      curve(t0, t1, t2, t3, trunkW * wide, trunkW * wide * taper, flare);
      if (flare <= 0) return;
      // Roots: a few each side, out from the foot of the trunk and down into the ground, however far it falls away.
      const rr = rng(seed ^ 0x5007);
      const w = trunkW * wide;
      for (const side of [-1, 1]) {
        const n = 2 + Math.floor(rr() * 2);
        for (let k = 0; k < n; k++) {
          const from = { x: cx + side * w * (0.15 + 0.6 * rr()), y: H - unit * (0.1 + 0.5 * rr()) };
          const reach = w * Math.min(flare, 1.2) * (1.2 + 1.4 * rr());
          const footX = from.x + side * reach;
          const foot = { x: footX, y: groundY(footX) + unit * 0.4 };
          const w0 = w * Math.min(flare, 1.2) * (0.3 + 0.2 * rr());
          curve(from, { x: from.x + side * reach * 0.45, y: from.y + unit * 0.1 }, { x: footX - side * reach * 0.15, y: (from.y + foot.y) / 2 }, foot, w0, Math.max(DETAIL, w0 * 0.3));
        }
      }
    },
    curve,
    limb: (from, a, len, w0, w1, bend = 0) => {
      const c1 = add(from, dir(a), len * 0.35);
      const c2 = add(c1, dir(a + bend * 0.5), len * 0.35);
      const end = add(c2, dir(a + bend), len * 0.3);
      curve(from, c1, c2, end, w0, w1);
      return end;
    },
    mass: (x, y, rx, ry) => void masses.push({ x, y, rx, ry }),
    glow: (x, y) => void glows.push({ x, y }),
    ground: groundY,
  };
  sp.build(b);

  const kind = new Uint8Array(W * HH);
  const toneOf = new Uint8Array(W * HH);

  // ----- wood, the trunk last over the bases of the branches growing out of it: flat bands with streaks of grain
  pieces.sort((p, q) => (p.flare ? 1 : 0) - (q.flare ? 1 : 0));
  for (const br of pieces) {
    const len = Math.hypot(br.x1 - br.x0, br.y1 - br.y0) || 1;
    const ux = (br.x1 - br.x0) / len;
    const uy = (br.y1 - br.y0) / len;
    const lit = Math.max(-1, Math.min(1, ((-uy * -1 + ux * -1) / Math.SQRT2) * 1.6));
    const pad = Math.max(br.w0, br.w1) * 2 + 2;
    for (let y = Math.max(0, Math.floor(Math.min(br.y0, br.y1) - pad)); y <= Math.min((br.flare ? H : HH) - 1, Math.ceil(Math.max(br.y0, br.y1) + pad)); y++) {
      for (let x = Math.max(0, Math.floor(Math.min(br.x0, br.x1) - pad)); x <= Math.min(W - 1, Math.ceil(Math.max(br.x0, br.x1) + pad)); x++) {
        const px = x + 0.5 - br.x0;
        const py = y + 0.5 - br.y0;
        const along = px * ux + py * uy;
        const t = Math.max(0, Math.min(1, along / len));
        const s = br.s0 + Math.max(0, Math.min(len, along));
        const flare = br.flare ? 1 + 1.1 * Math.max(0, 1 - s / br.flare) ** 2 : 1;
        const w = (br.w0 + (br.w1 - br.w0) * t) * flare;
        const side = px * -uy + py * ux;
        if (along < 0 || along > len) {
          if (Math.hypot(along < 0 ? along : along - len, side) > w) continue;
        } else if (Math.abs(side) > w) continue;
        const u = (side / w) * lit;
        const i = y * W + x;
        kind[i] = WOOD;
        let tone = u > 0.4 ? 3 : u > -0.3 ? 2 : u > -0.8 ? 1 : 0;
        const lane = Math.floor((side + w) / (2 * DETAIL));
        const streak = Math.sin(Math.floor(s / (5 * DETAIL) + lane * 7.31) * 12.9898 + lane * 78.233) * 43758.5453;
        if (tone > 1 && (side + w) % (2 * DETAIL) < DETAIL * 0.6 && streak - Math.floor(streak) > 0.45) tone--;
        if (sp.marks && Math.sin((y * 0.9 + x * 0.2) / DETAIL) > 0.85) tone = Math.min(tone, 1);
        toneOf[i] = tone;
      }
    }
  }
  // The foot of the trunk stops at the ground (the root row); where the ground falls away under it, its wood runs on down to meet it.
  for (let x = 0; x < W; x++) {
    const foot = (H - 1) * W + x;
    if (kind[foot] !== WOOD) continue;
    for (let y = H; y < Math.min(HH, groundY(x + 0.5) + DETAIL * 2); y++) {
      if (kind[y * W + x] === WOOD) continue;
      kind[y * W + x] = WOOD;
      toneOf[y * W + x] = toneOf[foot]!;
    }
  }

  // ----- leaves
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const m of masses) {
    minX = Math.min(minX, m.x - m.rx);
    maxX = Math.max(maxX, m.x + m.rx);
    minY = Math.min(minY, m.y - m.ry);
    maxY = Math.max(maxY, m.y + m.ry);
  }
  const crownLight = (x: number, y: number) => (-(x - (minX + maxX) / 2) / (maxX - minX + 1)) * 0.9 - ((y - (minY + maxY) / 2) / (maxY - minY + 1)) * 1.3;
  const put = (x: number, y: number, tone: number) => {
    if (x < 0 || y < 0 || x >= W || y >= HH) return;
    kind[y * W + x] = LEAF;
    toneOf[y * W + x] = tone;
  };

  const leaves: { x: number; y: number; angle: number; len: number }[] = [];
  if (sp.foliage === "cap") {
    // A mushroom's cap: a smooth dome lit from the upper left, with pale spots, a rim, and gills under it.
    const m = masses[0]!;
    const spots: [number, number, number][] = [];
    for (let k = 0; k < 7; k++) spots.push([m.x + range(-0.8, 0.8) * m.rx, m.y - range(0, 0.7) * m.ry, unit * range(0.35, 0.7)]);
    for (let y = Math.floor(m.y - m.ry); y <= Math.ceil(m.y + m.ry * 0.35); y++) {
      for (let x = Math.floor(m.x - m.rx); x <= Math.ceil(m.x + m.rx); x++) {
        const dx = (x + 0.5 - m.x) / m.rx;
        const dy = (y + 0.5 - m.y) / m.ry;
        if (dy > 0) {
          if (dy > 0.35 || dx * dx > 1 - dy * 0.6) continue;
          put(x, y, dy < 0.08 ? 1 : (x >> 1) % 3 === 0 ? 1 : 2);
          continue;
        }
        if (dx * dx + dy * dy > 1) continue;
        let k = 0.62 - dx * 0.3 + dy * 0.5 - (dx * dx + dy * dy) * 0.5;
        if (spots.some(([sx, sy, sr]) => Math.hypot(x - sx, (y - sy) * 1.4) < sr)) k = 0.95;
        else if (spots.some(([sx, sy, sr]) => Math.hypot(x - sx, (y - sy) * 1.4) < sr + 1.5)) k = 0.05;
        const t = Math.max(1, Math.min(5, Math.floor(1 + k * 5 + BAYER[(y & 3) * 4 + (x & 3)]! * 0.6)));
        put(x, y, t);
      }
    }
  } else if (sp.foliage !== "none") {
    // Outlined, two-tone leaves (as the vines' are) filling the masses, the back (lower, right) ones first.
    const lf = sp.leaf ?? { len: 0.62, wide: 1 };
    const leafLen = PX * lf.len;
    const massOf = (x: number, y: number) => masses.find((m) => ((x - m.x) / m.rx) ** 2 + ((y - m.y) / m.ry) ** 2 <= 1);
    const area = masses.reduce((n, m) => n + Math.PI * m.rx * m.ry, 0);
    const want = Math.round((area / (leafLen * leafLen * 0.3 * lf.wide)) * 0.65);
    for (let k = 0, tries = 0; k < want && tries < want * 4; tries++) {
      const x = range(minX, maxX);
      const y = range(minY, maxY);
      const m = massOf(x, y);
      if (!m) continue;
      k++;
      let angle = Math.atan2(y - m.y, x - m.x) + range(-0.7, 0.7);
      if (lf.hang) angle = Math.PI / 2 + range(-0.35, 0.35);
      else if (lf.up) angle = UP + range(-0.6, 0.6);
      else if (lf.needles) angle = (x >= m.x ? 0 : Math.PI) + (x >= m.x ? 1 : -1) * range(0.15, 0.6);
      leaves.push({ x, y, angle, len: leafLen * range(0.75, 1.15) });
    }
    leaves.sort((p, q) => q.y + q.x * 0.3 - (p.y + p.x * 0.3));
    const PROFILE = [0, 0, 0.15, 0.22, 0.4, 0.3, 0.7, 0.2, 1, 0];
    const SHAPE = Float32Array.from({ length: 65 }, (_, s) => {
      const f = s / 64;
      for (let k = 2; k < PROFILE.length; k += 2) {
        if (f <= PROFILE[k]!) {
          const f0 = PROFILE[k - 2]!;
          const w0 = PROFILE[k - 1]!;
          return w0 + ((PROFILE[k + 1]! - w0) * (f - f0)) / (PROFILE[k]! - f0);
        }
      }
      return 0;
    });
    const rim = 1.4;
    for (const leaf of leaves) {
      const ca = Math.cos(leaf.angle);
      const sa = Math.sin(leaf.angle);
      const light = crownLight(leaf.x, leaf.y) + range(-0.15, 0.15);
      const [hi, lo] = light > 0.3 ? [5, 3] : light > -0.25 ? [4, 2] : [3, 1];
      const wide = 0.3 * leaf.len * lf.wide + rim + 1;
      const tipX = leaf.x + ca * leaf.len;
      const tipY = leaf.y + sa * leaf.len;
      const x0 = Math.max(0, Math.floor(Math.min(leaf.x, tipX) - wide));
      const x1 = Math.min(W - 1, Math.ceil(Math.max(leaf.x, tipX) + wide));
      const y0 = Math.max(0, Math.floor(Math.min(leaf.y, tipY) - wide));
      const y1 = Math.min(HH - 1, Math.ceil(Math.max(leaf.y, tipY) + wide));
      const lo0 = -rim / leaf.len;
      const hi0 = 1 + rim / leaf.len;
      const scale = leaf.len * lf.wide;
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const dx = x + 0.5 - leaf.x;
          const dy = y + 0.5 - leaf.y;
          const along = dx * ca + dy * sa;
          const f = along / leaf.len;
          if (f < lo0 || f > hi0) continue;
          const across = -dx * sa + dy * ca;
          const half = SHAPE[Math.round((f < 0 ? 0 : f > 1 ? 1 : f) * 64)]! * scale;
          if (Math.abs(across) > half + rim) continue;
          put(x, y, f < 0 || f > 1 || Math.abs(across) > half ? 0 : across < 0 ? hi : lo);
        }
      }
    }
  }

  // Glowing points the shape put: fruit, embers, buds.
  for (const g of glows) {
    const fx = Math.round(g.x);
    const fy = Math.round(g.y);
    const R = DETAIL * 1.5;
    for (let y = fy - 3; y <= fy + 3; y++) for (let x = fx - 3; x <= fx + 3; x++) if (x >= 0 && y >= 0 && x < W && y < HH && Math.hypot(x - fx, y - fy) <= R) kind[y * W + x] = FRUIT;
  }

  // Glowing fruit with no say in where: hanging in the crown, more toward its underside.
  if (pal.glow && !glows.length && leaves.length) {
    for (let k = Math.round(leaves.length * 0.05); k > 0; k--) {
      const lf = leaves[Math.floor(r() * leaves.length)]!;
      const fx = Math.round(lf.x);
      const fy = Math.round(lf.y + DETAIL * 2);
      for (let y = fy - DETAIL; y <= fy + DETAIL; y++) for (let x = fx - DETAIL; x <= fx + DETAIL; x++) if (x >= 0 && y >= 0 && x < W && y < HH && Math.abs(x - fx) + Math.abs(y - fy) <= DETAIL) kind[y * W + x] = FRUIT;
    }
  }

  // ----- crop to the tree
  let x0 = W, x1 = -1, y0 = HH, y1 = H - 1;
  for (let y = 0; y < HH; y++) {
    for (let x = 0; x < W; x++) {
      if (!kind[y * W + x]) continue;
      x0 = Math.min(x0, x);
      x1 = Math.max(x1, x);
      y0 = Math.min(y0, y);
      y1 = Math.max(y1, y);
    }
  }
  // One pixel of room for the outline.
  x0 = Math.max(0, x0 - 1);
  y0 = Math.max(0, y0 - 1);
  x1 = Math.min(W - 1, x1 + 1);
  y1 = Math.min(HH - 1, y1 + 1);
  const cw = x1 - x0 + 1;
  const ch = y1 - y0 + 1;

  // ----- colour
  const data = new Uint8ClampedArray(cw * ch * 4);
  const hex = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)] as const;
  const bark = pal.bark.map(hex);
  const leaf = pal.leaf.map(hex);
  const glow = pal.glow ? hex(pal.glow) : leaf[5]!;
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= W || y >= HH ? NONE : kind[y * W + x]!);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const i = y * W + x;
      const k = kind[i]!;
      if (k === NONE) continue;
      const outside = at(x - 1, y) === NONE || at(x + 1, y) === NONE || at(x, y - 1) === NONE || at(x, y + 1) === NONE;
      let c: readonly number[];
      if (k === FRUIT) c = at(x, y - 1) !== FRUIT && at(x - 1, y) !== FRUIT ? [255, 250, 225] : glow;
      else if (k === WOOD) {
        let under = false;
        for (let d = 1; d <= 3 * DETAIL && !under; d++) under = at(x, y - d) === LEAF;
        const tone = toneOf[i]!;
        c = outside ? bark[0]! : bark[under && tone > 1 ? tone - 1 : tone]!;
      } else c = outside || toneOf[i] === 0 ? leaf[0]! : leaf[toneOf[i]!]!;
      const j = ((y - y0) * cw + (x - x0)) * 4;
      data[j] = c[0]!;
      data[j + 1] = c[1]!;
      data[j + 2] = c[2]!;
      data[j + 3] = 255;
    }
  }
  return { data, width: cw, height: ch, rootX: cx - x0, rootY: H - y0 };
}
